import { useMemo } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import defaultStyles from "./Typography.module.scss";

type SizeVariant = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
export const ColorVariant = {
  primary: "primary",
  subdued: "subdued",
} as const;

export type ColorVariant = (typeof ColorVariant)[keyof typeof ColorVariant];

export interface TypographyStylingProps {
  children?: React.ReactNode;
  size?: SizeVariant | number;
  weight?: "normal" | "bold";
  color?: ColorVariant | string;
  className?: string;
}

export type TypographyProps<T extends React.ElementType> =
  TypographyStylingProps & Omit<React.ComponentPropsWithoutRef<T>, "style">;

/**
 * Typography component
 *
 * @param children - The text content of the component
 * @param size - The size of the text
 * @param weight - The weight of the text
 * @param color - The color of the text
 * @param className - Additional class names to add to the component
 */
export const Typography = <T extends React.ElementType = "p">({
  children,
  size = "md",
  weight = "normal",
  color = "primary",
  className,
  ...rest
}: TypographyProps<T>) => {
  // The idea here is to provide some set font sizes but also a scape hatch in case we want to set one specific ourselves
  const isNumericSize = useMemo(() => typeof size === "number", [size]);
  // same idea here we can set text and subdued but we can also set var(--primary-light)
  const isColorVariant = useMemo(
    () => Object.keys(ColorVariant).includes(color),
    [color]
  );

  const [block, cn] = useModuleBEM(defaultStyles, "Typography");

  const combinedClassName = [
    block,
    !isNumericSize && cn(`size-${size}`),
    cn(`font-${weight}`),
    isColorVariant && cn(`font-color-${color}`),
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const sizeStyle = useMemo(
    () => (isNumericSize ? { fontSize: `${size}px` } : {}),
    [size, isNumericSize]
  );

  const colorStyle = useMemo(
    () => (!isColorVariant ? { color } : {}),
    [color, isColorVariant]
  );

  const combinedStyle = useMemo(
    () => ({
      ...sizeStyle,
      ...colorStyle,
    }),
    [sizeStyle, colorStyle]
  );

  return (
    <p className={combinedClassName} style={combinedStyle} {...rest}>
      {children}
    </p>
  );
};
